import { put, takeLatest, all } from "redux-saga/effects";
import api from "../../Environment";
import { createNotification } from "react-redux-notify";
import {
  getErrorNotificationMessage,
  getSuccessNotificationMessage,
} from "../../components/helper/NotificationMessage";
import PostService from "../../helpers/services/content/post.service";
import { parseContentServiceError } from "../../helpers/services/content/error";
import {
  fetchVideoStoreItemsSuccess,
  fetchVideoStoreItemsFailure,
  fetchVideoStoreSuggestionsSuccess,
  fetchVideoStoreSuggestionsFailure,
  changeVideoStoreVisibilitySuccess,
  changeVideoStoreVisibilityFailure,
} from "../actions/VideoStoreAction";
import {
  FETCH_VIDEO_STORE_ITEMS_START,
  FETCH_VIDEO_STORE_SUGGESTIONS_START,
  CHANGE_VIDEO_STORE_VISIBILITY_START,
} from "../actions/ActionConstant";
import { showErrorComponent } from "../actions/ComponentAction";

function* fetchVideoStoreItemsAPI(action) {
  try {
    // Extract pagination and filter data from action
    const { page = 0, limit = 15, ...filters } = action.data || {};
    
    // Convert page and limit to skip and take for API
    // skip = page (exact page number)
    // take = limit (number of items to fetch)
    const payload = {
      skip: page,
      take: limit,
      ...filters,
    };

    const response = yield api.postMethod("paid_video_posts", payload);
    
    if (response.data.success) {
      yield put(fetchVideoStoreItemsSuccess(response.data.data));
    } else {
      // Check for error code 9562 (requires login) - error_code is at root level
      if (response.data.error_code === 9562) {
        // Don't show error notification for 9562, just store the error with error_code
        yield put(fetchVideoStoreItemsFailure({
          error: response.data.error,
          error_code: response.data.error_code
        }));
        return;
      }
      
      yield put(fetchVideoStoreItemsFailure(response.data.error));
      const notificationMessage = getErrorNotificationMessage(
        response.data.error
      );
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    // Check for error code 9562 in catch block - error_code is at root level
    if (error?.response?.data?.error_code === 9562) {
      // Don't show error notification for 9562, just store the error with error_code
      yield put(fetchVideoStoreItemsFailure({
        error: error.response.data.error,
        error_code: error.response.data.error_code
      }));
      return;
    }
    
    yield put(fetchVideoStoreItemsFailure(error));
    if (error?.response?.status !== 400) {
      yield put(
        showErrorComponent({
          error: error,
          status: error?.response?.status ? error?.response?.status : 500,
        })
      );
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* fetchVideoStoreSuggestionsAPI(action) {
  try {
    const { username, page = 0, limit = 10 } = action.data || {};

    const payload = {
      username,
      skip: page,
      take: limit,
      limit,
    };

    const response = yield api.postMethod("paid_video_suggestions", payload);

    if (response.data.success) {
      const responseData = response.data.data || {};
      const normalized = {
        ...responseData,
        page: responseData.page ?? page,
        limit: responseData.limit ?? limit,
      };
      yield put(fetchVideoStoreSuggestionsSuccess(normalized));
    } else {
      yield put(fetchVideoStoreSuggestionsFailure(response.data.error));
      const notificationMessage = getErrorNotificationMessage(response.data.error);
      yield put(createNotification(notificationMessage));
    }
  } catch (error) {
    yield put(fetchVideoStoreSuggestionsFailure(error));
    if (error?.response?.status !== 400) {
      yield put(
        showErrorComponent({
          error: error,
          status: error?.response?.status ? error?.response?.status : 500,
        })
      );
    }
    const notificationMessage = getErrorNotificationMessage(error.message);
    yield put(createNotification(notificationMessage));
  }
}

function* changeVideoStoreVisibilityAPI(action) {
  try {
    const { post_id, is_hidden, previous_is_hidden } = action.data || {};
    if (!post_id && post_id !== 0) {
      throw new Error("Invalid post_id");
    }
    yield PostService.changeVisibilityForVideoStore(post_id, { is_hidden: !!is_hidden });

    // We update the UI optimistically in the reducer on START.
    // Still dispatch SUCCESS so per-post loading state clears.
    yield put(
      changeVideoStoreVisibilitySuccess({
        post_id,
        is_hidden: !!is_hidden,
        previous_is_hidden: !!previous_is_hidden,
      })
    );

    const successMessage = is_hidden ? "Hidden from video store" : "Visible in video store";
    yield put(createNotification(getSuccessNotificationMessage(successMessage)));
  } catch (error) {
    const request = action.data || {};
    yield put(changeVideoStoreVisibilityFailure(error, request));

    const message = error?.response ? parseContentServiceError(error.response) : error.message;
    const notificationMessage = getErrorNotificationMessage(message);
    yield put(createNotification(notificationMessage));
  }
}

export default function* videoStoreSaga() {
  yield all([
    yield takeLatest(FETCH_VIDEO_STORE_ITEMS_START, fetchVideoStoreItemsAPI),
    yield takeLatest(FETCH_VIDEO_STORE_SUGGESTIONS_START, fetchVideoStoreSuggestionsAPI),
    yield takeLatest(CHANGE_VIDEO_STORE_VISIBILITY_START, changeVideoStoreVisibilityAPI),
  ]);
}

